import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Empty,
  Popconfirm,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Tree,
  Typography,
} from 'antd';
import type { DataNode, TreeProps } from 'antd/es/tree';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { menusApi } from '@api/menus';
import { NormalizedApiError } from '@api/errors';
import { ErrorCode } from '@shared/constants/errorCodes';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { MenuItemFormModal } from '../components/MenuItemFormModal';
import { applyTreeDrop, flattenForReorder } from '../utils/tree';
import type {
  MenuItemCreateInput,
  MenuItemNode,
  MenuItemUpdateInput,
  MenuReorderNode,
} from '../types';

const { Text } = Typography;

function signature(items: MenuItemNode[]): string {
  const walk = (nodes: MenuItemNode[], parent: string | null): string =>
    nodes.map((n, i) => `${n.id}:${parent ?? ''}:${i}[${walk(n.children, n.id)}]`).join(',');
  return walk(items, null);
}

interface ItemModalState {
  open: boolean;
  editing: MenuItemNode | null;
  parentId: string | null;
  parentLabel?: string;
}

export function MenuEditorPage() {
  const { t } = useTranslation(['menus', 'common', 'errors']);
  const td = t as unknown as (key: string) => string;
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManage = useCan(PERMISSIONS.MENU_MANAGE);

  const { data: menu, isLoading } = useQuery({
    queryKey: ['menu', id],
    queryFn: () => menusApi.get(id),
    enabled: !!id,
  });

  const serverItems = useMemo(() => menu?.items ?? [], [menu]);
  const baseKey = signature(serverItems);
  const [override, setOverride] = useState<{ key: string; items: MenuItemNode[] } | null>(null);
  const items = override && override.key === baseKey ? override.items : serverItems;

  const [itemModal, setItemModal] = useState<ItemModalState>({
    open: false,
    editing: null,
    parentId: null,
  });

  const invalidateMenu = async () => {
    await qc.invalidateQueries({ queryKey: ['menu', id] });
    await qc.invalidateQueries({ queryKey: ['menus'] });
  };

  const reorderMutation = useMutation({
    mutationFn: (nodes: MenuReorderNode[]) => menusApi.items.reorder(id, nodes),
    onSuccess: async () => {
      await invalidateMenu();
    },
    onError: (err) => {
      setOverride(null);
      if (err instanceof NormalizedApiError && err.code === ErrorCode.MENU_CYCLE) {
        toast.error(t('errors:MENU_CYCLE'));
        return;
      }
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const createItemMutation = useMutation({
    mutationFn: (input: MenuItemCreateInput) => menusApi.items.create(id, input),
    onSuccess: async () => {
      toast.success(t('item.toast.created'));
      setItemModal((s) => ({ ...s, open: false }));
      await invalidateMenu();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateItemMutation = useMutation({
    mutationFn: (vars: { itemId: string; input: MenuItemUpdateInput }) =>
      menusApi.items.update(vars.itemId, vars.input),
    onSuccess: async () => {
      toast.success(t('item.toast.updated'));
      setItemModal((s) => ({ ...s, open: false }));
      await invalidateMenu();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => menusApi.items.remove(itemId),
    onSuccess: async () => {
      toast.success(t('item.toast.deleted'));
      await invalidateMenu();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const onDrop: TreeProps['onDrop'] = (info) => {
    const dropKey = String(info.node.key);
    const dragKey = String(info.dragNode.key);
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
    const next = applyTreeDrop(items, {
      dragKey,
      dropKey,
      dropPosition,
      dropToGap: info.dropToGap,
    });
    if (next === items) return;
    setOverride({ key: baseKey, items: next });
    reorderMutation.mutate(flattenForReorder(next));
  };

  const openAddRoot = () =>
    setItemModal({ open: true, editing: null, parentId: null, parentLabel: undefined });
  const openAddChild = (parent: MenuItemNode) =>
    setItemModal({ open: true, editing: null, parentId: parent.id, parentLabel: parent.label });
  const openEditItem = (item: MenuItemNode) =>
    setItemModal({ open: true, editing: item, parentId: item.parentId });

  const treeData = useMemo<DataNode[]>(() => {
    const toNodes = (nodes: MenuItemNode[]): DataNode[] =>
      nodes.map((n) => ({
        key: n.id,
        title: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <span style={{ fontWeight: 500, opacity: n.isActive ? 1 : 0.5 }}>{n.label}</span>
              <Tag style={{ marginInlineStart: 0 }}>{td(`linkType.${n.linkType}`)}</Tag>
              <Text type="secondary" ellipsis style={{ fontSize: 12, maxWidth: 320 }}>
                {n.linkType === 'url' ? n.url : n.contentSlug}
              </Text>
              {!n.isActive ? <Tag color="default">{t('item.inactive')}</Tag> : null}
            </div>
            {canManage ? (
              <Space onClick={(e) => e.stopPropagation()}>
                <Tooltip title={t('item.addChild')}>
                  <Button size="small" icon={<Plus size={13} />} onClick={() => openAddChild(n)} />
                </Tooltip>
                <Tooltip title={t('common:actions.edit')}>
                  <Button size="small" icon={<Pencil size={13} />} onClick={() => openEditItem(n)} />
                </Tooltip>
                <Popconfirm
                  title={t('item.delete.title')}
                  description={t('item.delete.confirm', { name: n.label })}
                  okText={t('common:actions.delete')}
                  okButtonProps={{ danger: true }}
                  cancelText={t('common:actions.cancel')}
                  onConfirm={() => deleteItemMutation.mutate(n.id)}
                >
                  <Button size="small" danger icon={<Trash2 size={13} />} />
                </Popconfirm>
              </Space>
            ) : null}
          </div>
        ),
        children: n.children.length ? toNodes(n.children) : undefined,
      }));
    return toNodes(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, canManage, t]);

  return (
    <div>
      <PageHeader
        title={menu?.name ?? t('editor.title')}
        description={t('editor.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('title'), to: ROUTES.MENUS },
          { label: menu?.name ?? '…' },
        ]}
        actions={
          <Space>
            <Button icon={<ArrowLeft size={15} />} onClick={() => void navigate(ROUTES.MENUS)}>
              {t('editor.back')}
            </Button>
            {canManage ? (
              <Button type="primary" icon={<Plus size={16} />} onClick={openAddRoot}>
                {t('item.new')}
              </Button>
            ) : null}
          </Space>
        }
      />

      <Card>
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {menu ? (
              <Space size={8} wrap>
                <Tag>{menu.location ? td(`location.${menu.location}`) : '—'}</Tag>
                <Tag color={menu.status === 'Published' ? 'green' : 'default'}>
                  {menu.status === 'Published' ? t('status.published') : t('status.draft')}
                </Tag>
                {menu.variantLanguageCodes.map((code) => (
                  <Tag key={code} color="blue">
                    {code}
                  </Tag>
                ))}
              </Space>
            ) : null}

            <Alert
              type="info"
              showIcon
              message={t('editor.reorderHint')}
              style={{ background: 'var(--ds-surface-2)' }}
            />

            {items.length === 0 ? (
              <Empty description={t('item.empty')}>
                {canManage ? (
                  <Button type="primary" icon={<Plus size={16} />} onClick={openAddRoot}>
                    {t('item.new')}
                  </Button>
                ) : null}
              </Empty>
            ) : (
              <Tree
                treeData={treeData}
                draggable={canManage ? { icon: false } : false}
                onDrop={onDrop}
                blockNode
                selectable={false}
                showLine
                defaultExpandAll
                switcherIcon={<ChevronRight size={14} />}
                className="ds-category-tree"
              />
            )}
          </Space>
        )}
      </Card>

      <MenuItemFormModal
        open={itemModal.open}
        editing={itemModal.editing}
        parentLabel={itemModal.parentLabel}
        saving={createItemMutation.isPending || updateItemMutation.isPending}
        onCancel={() => setItemModal((s) => ({ ...s, open: false }))}
        onCreate={(input) =>
          createItemMutation.mutate({ ...input, parentId: itemModal.parentId, order: null })
        }
        onUpdate={(itemId, input) => updateItemMutation.mutate({ itemId, input })}
      />
    </div>
  );
}
